/* eslint-disable react/jsx-props-no-spreading */
import React from "react";
import styled from "@emotion/styled";
import { Theme } from "@emotion/react";
import { borderRadius, cardShadow, InteractiveElement, media } from "@iventis/styles";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { CommentComponent } from "./comment";
import { useCommentsService } from "./comments-service";
import { SkeletonListComment } from "./comment-list-skeleton";

export const CommentList = ({
    selectedCommentId,
    handleCloseClick,
    handleCommentClick,
    className,
}: {
    selectedCommentId: string;
    handleCommentClick: (commentId: string) => void;
    handleCloseClick: () => void;
    className?: string;
}) => {
    const { resource, getByResourceId, delete: deleteComment, loggedInUser } = useCommentsService();
    const resourceId = resource.id;

    const { data: comments, isLoading: commentsIsLoading } = useQuery(["comments", resourceId], async () => getByResourceId(resourceId));

    const translate = useIventisTranslate();

    const handleDeleteComment = async (commentId: string, resourceId: string) => {
        deleteComment(commentId, resourceId);
    };

    return (
        <StyledCommentListWrapper className={className}>
            <StyledHeader>
                {translate(Content.comments.commentList.title)}
                <StyledInteractiveElement onClick={handleCloseClick} data-testid="close-view-all-comments">
                    <FontAwesomeIcon icon={["far", "xmark"]} size="lg" />
                </StyledInteractiveElement>
            </StyledHeader>
            {commentsIsLoading ? (
                <SkeletonListComment />
            ) : (
                <div className="scrollable-comment-list">
                    {comments.map((comment) => (
                        <InteractiveElement data-testid="comment-list-item" as="a" key={comment.id} onClick={() => handleCommentClick(comment.id)}>
                            <StyledComment
                                handleDeleteClick={
                                    comment.userId === loggedInUser.id || loggedInUser.resourcePermission === Authorisation.Admin
                                        ? () => handleDeleteComment(comment.id, comment.resourceId)
                                        : undefined
                                }
                                className={comment.id === selectedCommentId ? "active" : ""}
                                inCommentList
                                key={comment.id}
                                {...comment}
                                dataTestId="comment-list-item-content"
                                disableImageButton
                            />
                        </InteractiveElement>
                    ))}
                </div>
            )}
        </StyledCommentListWrapper>
    );
};

const StyledCommentListWrapper = styled.div`
    ${media.medium} {
        width: 300px;
        padding: 15px;
        max-height: 85dvh;
        gap: 10px;
        height: auto;
        border-radius: ${borderRadius.input};
        box-shadow: ${cardShadow};
    }
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    background: ${({ theme }: { theme: Theme }) => theme.secondaryColors.blank};
    overflow-y: none;

    .scrollable-comment-list {
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        gap: 10px;
    }
`;

const StyledHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-left: 15px;
    border-bottom: 1px solid ${({ theme }: { theme: Theme }) => theme.tertiaryColors.headerBackground};
    ${media.medium} {
        border-bottom: none;
        padding-left: 10px;
    }
`;

export const StyledInteractiveElement = styled(InteractiveElement)`
    padding: 14px;
`;

const StyledComment = styled(CommentComponent)`
    border: none;
    box-shadow: none;
    width: 100%;
    padding: 15px;
    border-bottom: 1px solid ${({ theme }: { theme: Theme }) => theme.shades.lightBorder};
    ${media.medium} {
        box-shadow: none;
        border: 1px solid transparent;
        padding: 10px;
        &.active,
        &:hover {
            border: 1px solid ${({ theme }: { theme: Theme }) => theme.primaryColors.focus};
            background: ${({ theme }: { theme: Theme }) => theme.secondaryColors.light50};
        }
    }

    .comment-header,
    .comment-body {
        margin-bottom: 10px;
    }

    .comment-footer {
        padding: 0;
    }
`;
